import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { AUTHOR_TABLE_BASE } from '../table-configs';
import { autoEnglishName } from '../../../core/utils/transliterate.util';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-author-master',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, VioTablesV2Module],
  templateUrl: './author-master.component.html',
  styleUrl: './author-master.component.scss',
})
export class AuthorMasterComponent implements OnInit, OnDestroy {
  /** When true: skip list, start directly in "new author" form, emit events instead of navigating */
  @Input() overlayMode = false;
  @Output() authorCreated = new EventEmitter<string>();
  @Output() overlayDismiss = new EventEmitter<void>();

  view: 'list' | 'form' = 'list';

  // List state
  records: any[] = [];
  isListLoading = false;

  // Form state
  form!: FormGroup;
  isEditMode = false;
  editId: string | null = null;
  isSaving = false;
  successMessage = '';
  errorMessage = '';

  // Suggestion state
  nameSuggestions: any[] = [];
  englishNameSuggestions: any[] = [];

  // Delete state
  showDeleteConfirm: string | null = null;
  isDeleting = false;
  deleteErrorMessage = '';

  // Author books modal
  showAuthorBooksModal = false;
  selectedAuthorForBooks: any = null;
  authorBooks: any[] = [];
  isLoadingAuthorBooks = false;
  authorBooksError = '';

  // Copies modal (stacks over author books modal)
  showCopiesModal = false;
  selectedCopiesBook: any = null;

  private destroy$ = new Subject<void>();

  tableConfig: VT2_TABLE_CONFIG = {
    ...AUTHOR_TABLE_BASE,
    actionButtons: [
      { icon: '✏', title: 'Edit', cssClass: 'vt2-icon-btn--edit', onClick: (row) => this.editRecord(row) },
      { icon: '📚', title: 'Books & Locations', cssClass: 'vt2-icon-btn--view', onClick: (row) => this.openAuthorBooks(row) },
    ],
  };

  constructor(private fb: FormBuilder, private api: ApiService, private pageTitle: PageTitleService, private dateFmt: DateFormatService) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.buildForm();
    if (this.overlayMode) {
      this.view = 'form';
    } else {
      this.pageTitle.set('Author Master', 'Manage authors in the system');
      this.loadList();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildForm(): void {
    this.form = this.fb.group({
      authorName:        ['', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      authorEnglishName: ['', [Validators.maxLength(150)]],
      authorAddress1:    [''],
      authorAddress2:    [''],
      authorAddress3:    [''],
      pincode:           ['', [Validators.pattern(/^\d{6}$/)]],
      mobileNo:          ['', [Validators.pattern(/^\d{10}$/)]],
      dateOfBirth:       [null],
    });

    this.form.get('authorName')!.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(val => {
      if (val && val.trim().length >= 2) {
        this.api.getAuthors({ search: val.trim(), field: 'authorName' }).subscribe({
          next: (res) => { this.nameSuggestions = (res || []).slice(0, 5); },
          error: () => { this.nameSuggestions = []; }
        });
      } else {
        this.nameSuggestions = [];
      }
    });

    this.form.get('authorEnglishName')!.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(val => {
      if (val && val.trim().length >= 2) {
        this.api.getAuthors({ search: val.trim(), field: 'authorEnglishName' }).subscribe({
          next: (res) => { this.englishNameSuggestions = (res || []).slice(0, 5); },
          error: () => { this.englishNameSuggestions = []; }
        });
      } else {
        this.englishNameSuggestions = [];
      }
    });
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getAuthors().subscribe({
      next: (data) => {
        this.records = data || [];
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  addNew(): void {
    this.isEditMode = false;
    this.editId = null;
    this.form.reset({}, { emitEvent: false });
    this.successMessage = '';
    this.errorMessage = '';
    this.nameSuggestions = [];
    this.englishNameSuggestions = [];
    this.view = 'form';
    if (!this.overlayMode) this.pageTitle.set('Author Master', 'Add a new author');
  }

  editRecord(record: any): void {
    this.isEditMode = true;
    this.editId = record._id || record.id;
    this.successMessage = '';
    this.errorMessage = '';
    this.nameSuggestions = [];
    this.englishNameSuggestions = [];
    this.form.patchValue({
      authorName:        record.authorName,
      authorEnglishName: record.authorEnglishName || '',
      authorAddress1:    record.authorAddress1 || '',
      authorAddress2:    record.authorAddress2 || '',
      authorAddress3:    record.authorAddress3 || '',
      pincode:           record.pincode || '',
      mobileNo:          record.mobileNo || '',
      dateOfBirth:       record.dateOfBirth ? record.dateOfBirth.split('T')[0] : null,
    }, { emitEvent: false });
    this.view = 'form';
    if (!this.overlayMode) this.pageTitle.set('Author Master', 'Update author information');
  }

  backToList(): void {
    this.nameSuggestions = [];
    this.englishNameSuggestions = [];
    if (this.overlayMode) {
      this.overlayDismiss.emit();
      return;
    }
    this.view = 'list';
    this.pageTitle.set('Author Master', 'Manage authors in the system');
    this.loadList();
  }

  resetForm(): void {
    this.form.reset({}, { emitEvent: false });
    this.nameSuggestions = [];
    this.englishNameSuggestions = [];
  }

  dismissNameSuggestions(): void { this.nameSuggestions = []; }
  dismissEnglishNameSuggestions(): void { this.englishNameSuggestions = []; }

  onAuthorNameBlur(): void {
    const name = (this.form.get('authorName')!.value || '').trim();
    if (!name) return;

    const transliterated = autoEnglishName(name);
    if (transliterated) {
      // Devanagari input — use transliterated result
      this.form.get('authorEnglishName')!.setValue(transliterated);
    } else if (/^[a-zA-Z\s'.\-,]+$/.test(name)) {
      // Already English — copy as-is
      this.form.get('authorEnglishName')!.setValue(name);
    }
    // emitEvent defaults to true → valueChanges fires → debounced search on authorEnglishName
  }

  get f() { return this.form.controls; }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  todayString(): string {
    return new Date().toISOString().split('T')[0];
  }

  confirmDelete(id: string): void {
    this.showDeleteConfirm = id;
    this.deleteErrorMessage = '';
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
    this.deleteErrorMessage = '';
  }

  executeDelete(id: string): void {
    this.isDeleting = true;
    this.deleteErrorMessage = '';
    this.api.deleteAuthor(id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = null;
        this.loadList();
      },
      error: (err: any) => {
        this.isDeleting = false;
        this.deleteErrorMessage = err?.message || 'Failed to delete author.';
      },
    });
  }

  openAuthorBooks(author: any): void {
    this.selectedAuthorForBooks = author;
    this.authorBooks = [];
    this.authorBooksError = '';
    this.showCopiesModal = false;
    this.isLoadingAuthorBooks = true;
    this.showAuthorBooksModal = true;
    this.api.locateBooksByAuthor(author.authorName).subscribe({
      next: (books) => {
        this.authorBooks = books || [];
        this.isLoadingAuthorBooks = false;
      },
      error: (err: any) => {
        this.authorBooksError = err?.message || 'Failed to load books.';
        this.isLoadingAuthorBooks = false;
      },
    });
  }

  closeAuthorBooksModal(): void {
    this.showAuthorBooksModal = false;
    this.showCopiesModal = false;
    this.selectedAuthorForBooks = null;
  }

  openCopiesModal(book: any): void {
    this.selectedCopiesBook = book;
    this.showCopiesModal = true;
  }

  closeCopiesModal(): void {
    this.showCopiesModal = false;
    this.selectedCopiesBook = null;
  }

  authorBookStatusLabel(status: string): string {
    if (status === 'C') return 'At Center';
    if (status === 'M') return 'Issued';
    if (status === 'S') return 'Scrapped';
    return status;
  }

  authorBookRowClass(status: string): string {
    if (status === 'C') return 'ab-row-c';
    if (status === 'M') return 'ab-row-m';
    if (status === 'S') return 'ab-row-s';
    return '';
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.successMessage = '';
    this.errorMessage = '';

    const payload = { ...this.form.value };

    const request$ = this.isEditMode
      ? this.api.updateAuthor({ ...payload, id: this.editId })
      : this.api.saveAuthor(payload);

    const savedName = (this.form.get('authorName')!.value || '').trim();

    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.successMessage = this.isEditMode ? 'Author updated successfully!' : 'Author saved successfully!';
        if (this.overlayMode && !this.isEditMode) {
          setTimeout(() => this.authorCreated.emit(savedName), 700);
        } else {
          setTimeout(() => this.backToList(), 1000);
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to save author.';
      },
    });
  }
}
